import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import css from './views.module.css'
import { Button, Card, Disclosure, MicroLabel, Notice } from '../ui'
import { parseMarkdown, plainText, splitSections, type Block, type DocSection } from '../lib/markdown'
import { short } from '../lib/text'
import { Blocks } from './Markdown'
import source from '../content/runbook.md?raw'

/**
 * The operator runbook, in the tool rather than in a file someone has to find.
 *
 * The document stays markdown in `src/content/runbook.md` so it can be edited
 * as prose and reviewed as a diff. This view renders it to React elements via
 * a small parser -- never to HTML -- and adds the three things a page can do
 * that a document cannot: jump to a section and link someone straight to it,
 * copy a command without selecting it, and tick off the handover checklist.
 */

const CHECKLIST_KEY = 'fleet.runbook.handover'

/** "Read the first two sections before touching anything. The rest is reference." */
const OPEN_ON_ARRIVAL = 2

/** The section a link lands in: its own heading, or one of its subsections'. */
function sectionOf(sections: ReadonlyArray<DocSection>, id: string | null): string | null {
  if (!id) return null
  return sections.find((s) => s.id === id || s.anchors.includes(id))?.id ?? null
}

const checklistItems = (blocks: ReadonlyArray<Block>) =>
  blocks.flatMap((block) => (block.kind === 'checklist' ? block.items : []))

export function Runbook() {
  const { blocks, sections } = useMemo(() => parseMarkdown(source), [])
  const { lead, sections: parts } = useMemo(() => splitSections(blocks), [blocks])
  const [params, setParams] = useSearchParams()
  const current = params.get('s')

  // Which sections are open. The two the document says to read first, plus
  // whichever one a link landed in -- a link to a closed section would scroll
  // to a heading and show nothing under it.
  const [opened, setOpened] = useState<ReadonlySet<string>>(() => {
    const initial = new Set(parts.slice(0, OPEN_ON_ARRIVAL).map((p) => p.id))
    const landed = sectionOf(parts, current)
    if (landed) initial.add(landed)
    return initial
  })
  const setOpen = useCallback((id: string, open: boolean) => {
    setOpened((state) => {
      const next = new Set(state)
      if (open) next.add(id)
      else next.delete(id)
      return next
    })
  }, [])
  const allOpen = opened.size === parts.length

  const [done, setDone] = useState<Record<string, boolean>>(() => {
    try {
      const stored = localStorage.getItem(CHECKLIST_KEY)
      return stored ? (JSON.parse(stored) as Record<string, boolean>) : {}
    } catch {
      // Private window, blocked storage. The checklist still ticks for this
      // session; it just will not be here tomorrow.
      return {}
    }
  })

  useEffect(() => {
    try {
      localStorage.setItem(CHECKLIST_KEY, JSON.stringify(done))
    } catch {
      /* nothing to do, and nothing worth interrupting anyone about */
    }
  }, [done])

  // A section in the URL is what makes "read the inbox section" a link you can
  // send rather than an instruction to scroll.
  useEffect(() => {
    if (!current) return
    const target = document.getElementById(current)
    if (target) target.scrollIntoView({ block: 'start', behavior: 'auto' })
  }, [current])

  const goTo = useCallback(
    (id: string) => {
      const next = new URLSearchParams(params)
      next.set('s', id)
      setParams(next, { replace: true })
      // Opened in the same render the URL changes in, so by the time the effect
      // above scrolls, a subsection's heading is on the page to scroll to.
      const section = sectionOf(parts, id)
      if (section) setOpen(section, true)
      // The same entry again changes no URL and runs no effect; scroll once it has opened.
      if (id === current) {
        requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView({ block: 'start', behavior: 'smooth' }))
      }
    },
    [params, setParams, parts, setOpen, current],
  )

  const checklistTotal = blocks.reduce(
    (total, block) => total + (block.kind === 'checklist' ? block.items.length : 0),
    0,
  )
  const checklistDone = blocks.reduce(
    (total, block) =>
      total + (block.kind === 'checklist' ? block.items.filter((item) => done[item.id]).length : 0),
    0,
  )

  return (
    <div className={css.runbookGrid}>
      <div className={css.runbookBody}>
        <Blocks blocks={lead} />
        <div className={css.runbookSections}>
          {parts.map((part) => {
            const items = checklistItems(part.blocks)
            const firstParagraph = part.blocks.find((b) => b.kind === 'paragraph')
            const isOpen = opened.has(part.id)
            // What is in it, closed: its subsections by name, the checklist's
            // progress, or failing both the opening of its first paragraph --
            // which, open, would be printed twice, so it goes.
            const summary =
              items.length > 0 ? `${items.filter((item) => done[item.id]).length} of ${items.length} ticked`
              : part.subsections.length > 0 ? part.subsections.join(' · ')
              : firstParagraph?.kind === 'paragraph' && !isOpen ? short(plainText(firstParagraph.content), 140)
              : null
            return (
              <Disclosure
                key={part.id}
                id={part.id}
                flush
                title={<span className={css.runbookSectionTitle}>{part.title}</span>}
                summary={summary}
                open={isOpen}
                onOpenChange={(open) => setOpen(part.id, open)}
              >
                <div className={css.runbookSectionBody}>
                  <Blocks
                    blocks={part.blocks}
                    done={done}
                    onToggle={(id) => setDone((state) => ({ ...state, [id]: !state[id] }))}
                  />
                </div>
              </Disclosure>
            )
          })}
        </div>
        <Notice>
          This page renders <span className="mono">src/content/runbook.md</span>. Edit the document
          and the page follows — it is prose in the repository, not content in a database.
        </Notice>
      </div>

      <div className={css.contents}>
        <Card style={{ padding: '13px 10px 13px 14px' }}>
          <div className={css.contentsHead}>
            <MicroLabel>Contents</MicroLabel>
            <Button
              variant="quiet"
              onClick={() => setOpened(allOpen ? new Set() : new Set(parts.map((p) => p.id)))}
              style={{ fontSize: 12, padding: '2px 6px' }}
            >
              {allOpen ? 'Close all' : 'Open all'}
            </Button>
          </div>
          <nav className={css.contentsList}>
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => goTo(section.id)}
                className={[
                  css.contentsLink,
                  section.level === 3 ? css.contentsSub : '',
                  current === section.id ? css.contentsCurrent : '',
                ].filter(Boolean).join(' ')}
              >
                {section.title}
              </button>
            ))}
          </nav>
        </Card>

        {checklistTotal > 0 ? (
          <Card style={{ padding: '13px 14px', marginTop: 10 }}>
            <MicroLabel>Handover</MicroLabel>
            <div className="mono" style={{ fontSize: 14, fontWeight: 500, marginTop: 5 }}>
              {checklistDone} of {checklistTotal}
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4, lineHeight: 1.5 }}>
              {/* Said plainly: there is no table for this, and inventing one
                  would be a write this app does not have. */}
              Ticked in this browser only — it is your progress through the list, not a shared
              record.
            </div>
          </Card>
        ) : null}
      </div>
    </div>
  )
}
